// Per-tool policy helpers for the integration Tools editor. No React and no
// api client at runtime, so `node --test` covers it.
//
// A tool with no override takes its baseline: the binding default, except a
// tool flagged `requiresExplicitEnable` (an app's raw-request), whose baseline
// is "off" whatever the default. Mirrors boo-backend src/lib/tool-access.ts.
//
// API access is two rows flagged `apiAccess`: "full" (the app's raw-request)
// and "read" (`{app}:raw-request-read`). Full implies read: while full is not
// "off" the read row shows full's state; the read row's own override is kept
// and applies again once full is off.

import type { HubTool, ToolAccess, ToolState } from "./api.ts";

type ToolRef = Pick<HubTool, "id" | "requiresExplicitEnable" | "apiAccess">;

/** State a tool has when no override names it. */
export function baselineState(tool: ToolRef, defaultState: ToolState): ToolState {
  return tool.requiresExplicitEnable ? "off" : defaultState;
}

/** Effective state for one tool under a draft default + overrides. */
export function effectiveState(
  tool: ToolRef,
  defaultState: ToolState,
  overrides: Record<string, ToolState>,
): ToolState {
  return overrides[tool.id] ?? baselineState(tool, defaultState);
}

/**
 * Minimal policy for the listed tools: keeps an override only when it differs
 * from that tool's baseline, and drops overrides for tools the app no longer
 * lists.
 */
export function minimalToolAccess(
  tools: readonly ToolRef[],
  defaultState: ToolState,
  overrides: Record<string, ToolState>,
): ToolAccess {
  const pruned: Record<string, ToolState> = {};
  for (const tool of tools) {
    const st = overrides[tool.id];
    if (st !== undefined && st !== baselineState(tool, defaultState)) pruned[tool.id] = st;
  }
  return { default: defaultState, overrides: pruned };
}

/** True when two minimal policies differ. */
export function toolAccessDiffers(a: ToolAccess, b: ToolAccess): boolean {
  const aKeys = Object.keys(a.overrides);
  return (
    a.default !== b.default ||
    aKeys.length !== Object.keys(b.overrides).length ||
    aKeys.some((id) => a.overrides[id] !== b.overrides[id])
  );
}

/** Ready-made actions (counted and listed first) vs the API access rows. */
export function splitTools<T extends ToolRef>(tools: readonly T[]): { actions: T[]; apiAccess: T[] } {
  const actions = tools.filter((t) => t.apiAccess === undefined);
  const apiAccess = tools
    .filter((t) => t.apiAccess !== undefined)
    .sort((a, b) => (a.apiAccess === b.apiAccess ? 0 : a.apiAccess === "read" ? -1 : 1));
  return { actions, apiAccess };
}

/** True when the read row takes its state from an enabled full access row. */
export function readIncludedInFull(
  tool: ToolRef,
  tools: readonly ToolRef[],
  defaultState: ToolState,
  overrides: Record<string, ToolState>,
): boolean {
  if (tool.apiAccess !== "read") return false;
  const full = tools.find((t) => t.apiAccess === "full");
  return full !== undefined && effectiveState(full, defaultState, overrides) !== "off";
}

/** State a row shows: its effective state, except a read row covered by full access. */
export function displayedState(
  tool: ToolRef,
  tools: readonly ToolRef[],
  defaultState: ToolState,
  overrides: Record<string, ToolState>,
): ToolState {
  const full = tools.find((t) => t.apiAccess === "full");
  if (full && readIncludedInFull(tool, tools, defaultState, overrides)) {
    return effectiveState(full, defaultState, overrides);
  }
  return effectiveState(tool, defaultState, overrides);
}

// ── Dashboard copy ──────────────────────────────────────────────────────────
// Tools without any apiAccess row (older backends) keep the original copy.

/** Copy that frames the Tools editor: intro, the default card, the API access group. */
export function toolsEditorCopy(
  tools: readonly ToolRef[],
  appLabel: string,
): {
  intro: string;
  defaultTitle: string;
  defaultDescription: string;
  apiHeading: string;
  apiNote: string;
} {
  const apiRows = tools.filter((t) => t.apiAccess !== undefined);
  if (apiRows.length === 0) {
    return {
      intro: "Choose which tools Boos may use. Off tools are refused when a Boo asks for them.",
      defaultTitle: "Default for all tools",
      defaultDescription: "Applies to any tool you don’t set below, including ones this app adds later.",
      apiHeading: "",
      apiNote: "",
    };
  }
  // Exempt apps (google_drive, google_sheets): the API rows follow the default.
  const exempt = !apiRows.some((t) => t.requiresExplicitEnable);
  return {
    intro: `Pick what Boos can do in ${appLabel}.`,
    defaultTitle: "Default for all actions",
    defaultDescription: exempt
      ? "On means Boos can use every action and API access, including new ones."
      : "On means Boos can use every action, including new ones. API access below is separate.",
    apiHeading: "API access",
    apiNote: exempt
      ? "Lets Boos do things the actions above can't. Follows the default above."
      : "Lets Boos do things the actions above can't. Starts off. Turn on only what Boos need.",
  };
}

/** Title + description for an API access row (read, else full); the hub's own name/description is not used. */
export function apiAccessRowCopy(tool: ToolRef, appLabel: string): { title: string; description: string } {
  return tool.apiAccess === "read"
    ? {
        title: "Read-only API access",
        description: `Boos can see anything this account can see in ${appLabel}. Some apps need full access even to search.`,
      }
    : {
        title: "Full API access",
        description: `Boos can add, change and delete anything this account can in ${appLabel}.`,
      };
}

/** Hint on the read row while full access covers it. */
export const READ_INCLUDED_HINT = "On, because full access is on.";

export type ApiAccessLevel = "off" | "read-only" | "full";

/** Highest API access level on, from the rows' displayed states. */
export function apiAccessLevel(
  tools: readonly ToolRef[],
  defaultState: ToolState,
  overrides: Record<string, ToolState>,
): ApiAccessLevel {
  const on = (kind: "read" | "full") =>
    tools.some((t) => t.apiAccess === kind && displayedState(t, tools, defaultState, overrides) !== "off");
  return on("full") ? "full" : on("read") ? "read-only" : "off";
}

/** Footer counter; `level` is null when the app has no API access rows. */
export function toolsFooterSummary(onCount: number, actionCount: number, level: ApiAccessLevel | null): string {
  return level === null
    ? `${onCount} of ${actionCount} on`
    : `${onCount} of ${actionCount} actions on · API access: ${level}`;
}
